﻿import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Heart, HeartCrack, Snowflake, Swords } from 'lucide-react';
import { useParams, useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import type { JukeboxOutletContext } from './JukeboxLayout';
import { useJukebox } from '../hooks/useJukebox';
import { useSugestaoBusca } from '../hooks/useSugestaoBusca';
import { useLogo } from '@/shared/hooks/useLogo';
import { NowPlayingCard } from '../components/NowPlayingCard';
import { TvOfflineOverlay } from '../components/TvOfflineOverlay';
import { PedidoIniciadoPopup } from '../components/PedidoIniciadoPopup';
import { PedidoConfirmado } from '../components/PedidoConfirmado';
import { MobileSheet } from '@/shared/components/MobileSheet';
import { JukeboxHeader } from '../components/JukeboxHeader';
import { JukeboxFilaSection } from '../components/JukeboxFilaSection';
import { JukeboxRepertorio } from '../components/JukeboxRepertorio';
import { JukeboxBuscaView } from '../components/JukeboxBuscaView';
import { resolverTema } from '../components/jukeboxTemaTokens';
import { useFutebolRealtime } from '@/features/futebol';
import { useReacaoEmissor } from '../hooks/useReacaoAoVivo';
import { useJukeboxPopupQueue } from '../hooks/useJukeboxPopupQueue';
import JukeboxJuninaBanner from '../components/JukeboxJuninaBanner';
import { TvDiaNamoradosTakeover, TvFestaJuninaTakeover, TvCopaTakeover, JukeboxDiaNamoradosBanner, JukeboxFestaJuninaBanner, JukeboxCopaBanner } from '@/features/temas-sazonais';
import { useJukeboxCliente } from '../contexts/JukeboxClienteContext';
import { calcularEtaFilaSegundos, formatarEtaMinutos } from '../../domain/usecases/calcularEtaFila';
import { filtrarItensTocaveis } from '../../domain/usecases/buscarMusicas';

function Toast({ msg }: { msg: string }) {
  return (
    <div className="fixed bottom-28 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2.5 rounded-2xl bg-red-500 px-5 py-3.5 text-sm font-semibold text-white shadow-2xl shadow-red-500/40">
      <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
      {msg}
    </div>
  );
}

export default function JukeboxCliente() {
  const { telaId } = useParams<{ telaId: string }>();
  const logoUrl    = useLogo();
  const { partida: partidaFutebol, jogo: jogoFutebol } = useFutebolRealtime(telaId ?? '');
  const { status: clienteStatus, cliente } = useJukeboxCliente();

  const {
    tela, tvOnline, sincronizando,
    items, filaAtiva, cooldowns,
    loading, pedindoId,
    toast, pedidoConfirmado, showPopup, agora,
    musicaAtual, pedidoIniciado, sugestaoAprovada, sugestaoRecusada, pdvToast,
    meuSessionId,
    handlePedir, refresh, dismissConfirmado, dismissPopup,
    pendingVibeItem, handlePedirComVibe, cancelarVibeSelection,
    batalhaVibesAtiva, meuTime,
    temaDiaNamorados, temaFestaJunina, temaCopa,
  } = useJukebox(
    telaId,
    clienteStatus === 'authenticated',
    clienteStatus === 'authenticated' && cliente
      ? { token: cliente.token }
      : null,
  );

  const { enviarReacao } = useReacaoEmissor(telaId);

  const outletCtx = useOutletContext<JukeboxOutletContext | undefined>();
  const tecladoAberto = outletCtx?.tecladoAberto ?? false;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const buscaAberta = searchParams.get('buscar') === '1';

  // Guarda se fomos nós que empurramos a entrada de histórico. Se o cliente
  // colou um link já com `?buscar=1`, não há para onde voltar — nesse caso
  // fechamos limpando o param, em vez de tirar o cliente do app.
  const abrimosBusca = useRef(false);

  const abrirBusca = useCallback(() => {
    if (buscaAberta) return;
    abrimosBusca.current = true;
    navigate({ search: '?buscar=1' });
  }, [buscaAberta, navigate]);

  const [visiveis, setVisiveis] = useState(15);
  const [sugestaoAprovadaDismissed, setSugestaoAprovadaDismissed] = useState(false);
  const [sugestaoRecusadaDismissed, setSugestaoRecusadaDismissed] = useState(false);

  // Propaga empresaId/tvOnline pro JukeboxLayout (nunca desmonta), que é
  // quem efetivamente renderiza o PolaroidCapture — assim o botão de foto
  // funciona em qualquer aba, não só na Home.
  useEffect(() => {
    outletCtx?.setEmpresaId(tela?.empresa_id ?? null);
  }, [outletCtx, tela?.empresa_id]);

  const observerRef = useRef<IntersectionObserver | null>(null);

  const PULL_THRESHOLD = 64;
  const [refreshing, setRefreshing] = useState(false);
  const [pullDist, setPullDist] = useState(0);
  const touchStartY = useRef(0);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (refreshing || tecladoAberto) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    const atTop = (scrollerRef.current?.scrollTop ?? 0) === 0;
    setPullDist(atTop && delta > 0 ? Math.min(delta, PULL_THRESHOLD) : 0);
  }, [refreshing, tecladoAberto]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (tecladoAberto) return;
    const delta = e.changedTouches[0].clientY - touchStartY.current;
    const atTop = (scrollerRef.current?.scrollTop ?? 0) === 0;
    setPullDist(0);
    if (atTop && delta > PULL_THRESHOLD && !refreshing) {
      setRefreshing(true);
      navigator.vibrate?.(30);
      refresh().finally(() => setRefreshing(false));
    }
  }, [refreshing, refresh, tecladoAberto]);

  const minhaPosicaoFila = useMemo(() => {
    if (filaAtiva.some(f => f.session_id === meuSessionId && f.status === 'tocando')) {
      return null;
    }
    const pendentes = filaAtiva.filter(f => f.status === 'pendente');
    const idx = pendentes.findIndex(f => f.session_id === meuSessionId);
    return idx >= 0 ? idx + 1 : null;
  }, [filaAtiva, meuSessionId]);

  const [agoraEta, setAgoraEta] = useState(() => Date.now());
  useEffect(() => {
    if (minhaPosicaoFila === null) return;
    setAgoraEta(Date.now());
    const id = setInterval(() => setAgoraEta(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [minhaPosicaoFila]);

  const etaLabel = useMemo(() => {
    const segundos = calcularEtaFilaSegundos(filaAtiva, meuSessionId, musicaAtual, agoraEta);
    if (segundos == null || segundos <= 0) return null;
    return formatarEtaMinutos(segundos);
  }, [filaAtiva, meuSessionId, musicaAtual, agoraEta]);

  const filaPendente = useMemo(() => {
    const tocando   = filaAtiva.filter(f => f.status === 'tocando');
    const pendentes = filaAtiva.filter(f => f.status === 'pendente');
    return [...tocando, ...pendentes];
  }, [filaAtiva]);

  const scoreBatalha = useMemo(() => {
    if (!batalhaVibesAtiva) return null;
    const romance   = filaAtiva.filter(f => f.vibe === 'romance').length;
    const sofrencia = filaAtiva.filter(f => f.vibe === 'sofrencia').length;
    return { romance, sofrencia };
  }, [filaAtiva, batalhaVibesAtiva]);

  const isInvasaoJunina = musicaAtual?.is_invasao_tematica === true;
  const temaResolvido = useMemo(
    () => resolverTema(isInvasaoJunina, temaDiaNamorados, temaFestaJunina, temaCopa),
    [isInvasaoJunina, temaDiaNamorados, temaFestaJunina, temaCopa],
  );

  const {
    resultadosBusca,
    status: statusSugestao,
    erro: erroSugestao,
    buscarNoYoutube,
    enviarSugestao,
    limparBusca: limparSugestoes,
  } = useSugestaoBusca(tela?.empresa_id ?? '', telaId ?? '', meuSessionId, cliente?.id);

  // O cliente só vê o que a TV consegue tocar. `items` (completo) segue na checagem
  // de duplicada de `enviarSugestao`, para o aviso "já está na playlist" continuar certo.
  const itemsTocaveis = useMemo(() => filtrarItensTocaveis(items), [items]);

  const enviarSugestaoSimples = useCallback(async (
    video: Parameters<typeof enviarSugestao>[0],
  ) => {
    await enviarSugestao(video, items);
  }, [enviarSugestao, items]);

  useEffect(() => { if (sugestaoAprovada) setSugestaoAprovadaDismissed(false); }, [sugestaoAprovada]);
  useEffect(() => { if (sugestaoRecusada) setSugestaoRecusadaDismissed(false); }, [sugestaoRecusada]);

  const popupAtivo = useJukeboxPopupQueue({
    vibeSelection:    !!pendingVibeItem,
    sugestaoAprovada: !!sugestaoAprovada && !sugestaoAprovadaDismissed,
    sugestaoRecusada: !!sugestaoRecusada && !sugestaoRecusadaDismissed,
    pedidoIniciado:   showPopup && !!pedidoIniciado,
    pedidoConfirmado: !!pedidoConfirmado,
    toast:            !!toast,
  });

  const fecharBusca = useCallback(() => {
    limparSugestoes();
    if (abrimosBusca.current) {
      abrimosBusca.current = false;
      navigate(-1);
      return;
    }
    navigate({ search: '' }, { replace: true });
  }, [navigate, limparSugestoes]);

  const sentinelRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) setVisiveis(prev => prev + 15); },
      { rootMargin: '300px' },
    );
    observerRef.current.observe(el);
  }, []);

  if (loading) {
    return (
      <div className="min-h-dvh bg-jukebox-bg">
        <div className="sticky top-0 z-20 bg-jukebox-bg/90 backdrop-blur-xl border-b border-white/[0.05] px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center gap-3">
          <div className="h-8 w-24 bg-white/[0.04] rounded-xl animate-pulse" />
          <div className="w-px h-5 bg-white/[0.05]" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-20 bg-white/[0.04] rounded-full animate-pulse" />
            <div className="h-2 w-32 bg-white/[0.03] rounded-full animate-pulse" />
          </div>
        </div>
        <div className="px-4 pt-5 space-y-5">
          <div className="rounded-3xl bg-white/[0.03] border border-white/[0.05] p-4 flex gap-3 items-center">
            <div className="w-16 h-16 rounded-2xl bg-white/[0.06] animate-pulse shrink-0" />
            <div className="flex-1 space-y-2.5">
              <div className="h-2 w-24 bg-white/[0.04] rounded-full animate-pulse" />
              <div className="h-3.5 w-40 bg-white/[0.05] rounded-full animate-pulse" />
              <div className="h-1 w-full bg-white/[0.03] rounded-full animate-pulse mt-3" />
            </div>
          </div>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/[0.05]">
              <div className="w-14 h-14 rounded-xl bg-white/[0.05] animate-pulse shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-36 bg-white/[0.04] rounded-full animate-pulse" />
                <div className="h-2.5 w-20 bg-white/[0.03] rounded-full animate-pulse" />
              </div>
              <div className="w-9 h-9 rounded-full bg-white/[0.04] animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!tela || !tela.playlist_id) {
    return (
      <div className="min-h-dvh bg-jukebox-bg flex items-center justify-center p-6">
        <div className="text-center max-w-xs">
          <div className="w-16 h-16 rounded-2xl bg-white/[0.04] flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-white font-semibold text-lg">Jukebox indisponível</h2>
          <p className="text-white/50 text-sm mt-2">Esta tela não possui uma playlist ativa no momento.</p>
        </div>
      </div>
    );
  }

  const tocandoVideoId  = musicaAtual?.youtube_video_id ?? null;
  const { temaNome, tema } = temaResolvido;

  return (
    <div
      ref={scrollerRef}
      className={`min-h-dvh pb-24 overflow-y-auto transition-colors duration-1000 ${tema.bodyBg}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {(refreshing || pullDist > 0) && (
        <div
          className="flex items-center justify-center gap-2 overflow-hidden transition-[height,opacity] duration-200"
          style={{
            height:  refreshing ? 40 : pullDist * 0.625,
            opacity: refreshing ? 1  : pullDist / PULL_THRESHOLD,
          }}
        >
          {refreshing ? (
            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg
              className="w-4 h-4 text-orange-500/70 transition-transform duration-100"
              style={{ transform: `rotate(${(pullDist / PULL_THRESHOLD) * 180}deg)` }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
          <span className="text-white/55 text-xs">
            {refreshing ? 'Atualizando...' : pullDist >= PULL_THRESHOLD ? 'Solte para atualizar' : 'Puxe para atualizar'}
          </span>
        </div>
      )}

      {!tvOnline && <TvOfflineOverlay telaNome={tela.nome} />}

      <JukeboxHeader
        logoUrl={logoUrl}
        telaNome={tela.nome}
        clienteNome={clienteStatus === 'authenticated' ? cliente?.nome : null}
        clienteAvatarUrl={cliente?.avatar_url}
        tema={tema}
        minhaPosicaoFila={minhaPosicaoFila}
        etaLabel={etaLabel}
        tvOnline={tvOnline}
      />

      {temaNome === 'namorados' && <TvDiaNamoradosTakeover hideBadge />}
      {temaNome === 'junina'    && <TvFestaJuninaTakeover  hideBadge />}
      {temaNome === 'copa'      && <TvCopaTakeover         hideBadge />}

      {isInvasaoJunina                          && <JukeboxJuninaBanner />}
      {temaNome === 'namorados'                 && <JukeboxDiaNamoradosBanner />}
      {temaNome === 'junina' && !isInvasaoJunina && <JukeboxFestaJuninaBanner />}
      {temaNome === 'copa'                      && <JukeboxCopaBanner partida={partidaFutebol} jogo={jogoFutebol} />}

      {batalhaVibesAtiva && (
        <div className="px-4 pt-3 animate-fade-in">
          <div className={`
            rounded-2xl px-4 py-3 border flex items-center gap-3
            ${meuTime === 'romance'   ? 'bg-rose-500/10 border-rose-500/25'
            : meuTime === 'sofrencia' ? 'bg-cyan-500/10 border-cyan-500/25'
            : 'bg-white/[0.05] border-white/[0.08]'}
          `}>
            {meuTime ? (
              <>
                <span className="shrink-0">
                  {meuTime === 'romance'
                    ? <Heart size={20} fill="currentColor" strokeWidth={0} className="text-rose-400" />
                    : <Snowflake size={20} className="text-cyan-400" />
                  }
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-black uppercase tracking-widest ${
                    meuTime === 'romance' ? 'text-rose-400' : 'text-cyan-400'
                  }`}>
                    {meuTime === 'romance' ? 'Time Apaixonado' : 'Time Sofrendo'}
                  </p>
                  <p className="text-white/50 text-[10px] mt-0.5">Batalha de Jukebox ativa</p>
                </div>
                {scoreBatalha && (scoreBatalha.romance + scoreBatalha.sofrencia) >= 1 && (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-rose-400 font-black text-sm tabular-nums">{scoreBatalha.romance}</span>
                    <span className="inline-flex items-center gap-0.5 text-white/50 text-[10px] font-bold">
                      <Heart size={8} fill="currentColor" strokeWidth={0} className="text-rose-400/40" />
                      VS
                      <HeartCrack size={8} fill="currentColor" strokeWidth={0} className="text-cyan-400/40" />
                    </span>
                    <span className="text-cyan-400 font-black text-sm tabular-nums">{scoreBatalha.sofrencia}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <Swords size={18} className="text-white/50 shrink-0" aria-hidden />
                <div className="flex-1 min-w-0">
                  <p className="text-white/80 text-xs font-bold">Batalha de Vibes ativa!</p>
                  <p className="text-white/50 text-[10px] mt-0.5">
                    Escolha seu time ao fazer um pedido{' '}
                    <span className="inline-flex items-center gap-0.5">
                      <Heart size={8} fill="currentColor" strokeWidth={0} className="text-rose-400/60" />
                      <Snowflake size={8} className="text-cyan-400/60" />
                    </span>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="px-4 pt-5 space-y-5">
        {musicaAtual ? (
          <NowPlayingCard
            titulo={musicaAtual.titulo}
            thumbnail={musicaAtual.thumbnail}
            meuPedido={filaAtiva.some(
              f => f.youtube_video_id === musicaAtual.youtube_video_id
                && f.session_id === meuSessionId
                && f.status === 'tocando',
            )}
            clienteNome={clienteStatus === 'authenticated' ? cliente?.nome : null}
            duracaoSegundos={musicaAtual.duracao_segundos}
            iniciadaEm={musicaAtual.iniciada_em}
            progressGradient={tema.progressGradient}
            nowPlayingGlow={tema.nowPlayingGlow}
            mostrarReacoes={tvOnline}
            temaCopa={temaCopa}
            onReagir={enviarReacao}
          />
        ) : sincronizando ? (
          <div className="relative overflow-hidden rounded-3xl bg-white/[0.03] border border-white/[0.05]">
            <div className="p-5 flex gap-4 items-center">
              <div className="shrink-0 w-24 h-24 rounded-2xl bg-white/[0.06] animate-pulse" />
              <div className="flex-1 space-y-3">
                <div className="h-5 w-20 bg-white/[0.06] rounded-full animate-pulse" />
                <div className="h-4 w-44 bg-white/[0.04] rounded-full animate-pulse" />
                <div className="h-1 w-full bg-white/[0.04] rounded-full animate-pulse mt-4" />
              </div>
            </div>
          </div>
        ) : (
          <div className="relative flex items-center gap-4 overflow-hidden rounded-3xl border border-white/[0.06] bg-white/[0.02] p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/[0.04] ring-1 ring-white/[0.06]">
              <svg className={`h-6 w-6 ${tema.emptyIcon}`} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" />
              </svg>
            </div>
            <div>
              <p className="text-[11px] font-medium text-white/50">Palco</p>
              <p className="mt-0.5 text-sm text-white/50">Nenhuma música no ar</p>
            </div>
          </div>
        )}

        <JukeboxFilaSection
          filaPendente={filaPendente}
          meuSessionId={meuSessionId}
          clienteNome={clienteStatus === 'authenticated' ? cliente?.nome : null}
          queueIcon={tema.queueIcon}
          etaLabel={etaLabel}
        />

        <JukeboxRepertorio
          items={itemsTocaveis}
          filaAtiva={filaAtiva}
          cooldowns={cooldowns}
          tocandoVideoId={tocandoVideoId}
          meuSessionId={meuSessionId}
          agora={agora}
          pedindoId={pedindoId}
          onPedir={handlePedir}
          batalhaVibesAtiva={batalhaVibesAtiva}
          meuTime={meuTime}
          tema={tema}
          visiveis={visiveis}
          sentinelRef={sentinelRef}
          onAbrirBusca={abrirBusca}
        />
      </div>

      {/* FAB — buscar / sugerir música (acima da bottom nav) */}
      {!buscaAberta && tvOnline && (
        <button
          type="button"
          onClick={abrirBusca}
          aria-label="Buscar ou sugerir música"
          className={`
            fixed bottom-24 right-4 z-20 flex h-14 w-14 items-center justify-center
            rounded-full bg-gradient-to-r shadow-lg transition-transform
            active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${tema.fabGradient}
          `}
        >
          <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}

      {buscaAberta && (
        <JukeboxBuscaView
          items={itemsTocaveis}
          filaAtiva={filaAtiva}
          cooldowns={cooldowns}
          tocandoVideoId={tocandoVideoId}
          meuSessionId={meuSessionId}
          agora={agora}
          pedindoId={pedindoId}
          onPedir={handlePedir}
          onFechar={fecharBusca}
          tema={tema}
          resultadosBusca={resultadosBusca}
          statusSugestao={statusSugestao}
          erroSugestao={erroSugestao}
          onBuscarYoutube={buscarNoYoutube}
          onEnviarSugestao={video => void enviarSugestaoSimples(video)}
          clienteNome={clienteStatus === 'authenticated' ? cliente?.nome : null}
        />
      )}

      {popupAtivo === 'toast' && toast && <Toast msg={toast} />}
      {pdvToast && (
        <div className="pointer-events-none fixed left-1/2 top-[calc(env(safe-area-inset-top)+12px)] z-40 -translate-x-1/2 rounded-2xl border border-white/[0.08] bg-jukebox-sheet px-4 py-2.5 text-sm font-semibold text-white shadow-2xl">
          🔥 {pdvToast}
        </div>
      )}

      {popupAtivo === 'pedidoConfirmado' && pedidoConfirmado && telaId && (
        <PedidoConfirmado
          item={pedidoConfirmado.item}
          posicao={pedidoConfirmado.posicao}
          etaLabel={etaLabel}
          onDismiss={dismissConfirmado}
        />
      )}

      {popupAtivo === 'pedidoIniciado' && showPopup && pedidoIniciado && (
        <PedidoIniciadoPopup
          titulo={pedidoIniciado.titulo}
          thumbnail={pedidoIniciado.thumbnail}
          clienteNome={clienteStatus === 'authenticated' ? cliente?.nome : null}
          onDismiss={dismissPopup}
        />
      )}

      <MobileSheet
        aberto={popupAtivo === 'sugestaoRecusada' && !!sugestaoRecusada && !sugestaoRecusadaDismissed}
        onClose={() => setSugestaoRecusadaDismissed(true)}
        titulo="Sugestão não aprovada"
      >
        <div className="px-6 pb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-2xl bg-red-500/15 border border-red-500/20 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-red-400 font-bold text-sm">Sugestão não aprovada</p>
              <p className="text-white/50 text-xs mt-0.5 line-clamp-2">{sugestaoRecusada?.titulo}</p>
            </div>
          </div>
          <button
            onClick={() => setSugestaoRecusadaDismissed(true)}
            className="w-full py-3.5 rounded-2xl bg-white/[0.06] text-white text-sm font-semibold active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-transform"
          >
            Entendi
          </button>
        </div>
      </MobileSheet>

      <MobileSheet
        aberto={popupAtivo === 'sugestaoAprovada' && !!sugestaoAprovada && !sugestaoAprovadaDismissed}
        onClose={() => setSugestaoAprovadaDismissed(true)}
        titulo="Sugestão aprovada"
      >
        <div className="px-6 pb-6">
          <div className="relative rounded-2xl overflow-hidden h-28 mb-5">
            <img
              src={sugestaoAprovada?.thumbnail}
              alt={sugestaoAprovada?.titulo}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 pointer-events-none">
              <motion.div
                className="absolute left-0 -top-2 -bottom-2 w-1/3 bg-gradient-to-r from-transparent via-white/[0.10] to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '200%' }}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-3 left-3 right-3">
              <p className="text-white font-bold text-sm line-clamp-2 leading-snug">{sugestaoAprovada?.titulo}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center shrink-0 animate-scale-in">
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-emerald-400 font-bold text-sm">Sugestão aprovada!</p>
              <p className="text-white/50 text-xs mt-0.5">Sua música foi para a fila e já vai tocar</p>
            </div>
          </div>
          <button
            onClick={() => setSugestaoAprovadaDismissed(true)}
            className="w-full py-3.5 rounded-2xl bg-white/[0.06] text-white text-sm font-semibold active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-transform"
          >
            Ótimo!
          </button>
        </div>
      </MobileSheet>

      <MobileSheet
        aberto={popupAtivo === 'vibeSelection' && !!pendingVibeItem}
        onClose={cancelarVibeSelection}
        titulo="Batalha de Jukebox"
      >
        <div className="px-6 pb-6">
          <div className="text-center mb-6">
            <p className="text-white/50 text-xs font-bold uppercase tracking-widest mb-1">Batalha de Jukebox</p>
            <h3 className="text-white font-black text-lg leading-tight">Você está pedindo como quem?</h3>
            <p className="text-white/50 text-xs mt-1 line-clamp-1">{pendingVibeItem?.titulo}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => void handlePedirComVibe('romance')}
              className="flex flex-col items-center gap-2 py-5 rounded-2xl bg-rose-500/10 border border-rose-500/30 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/60 transition-all animate-fade-in-up"
              style={{ animationDelay: '80ms', animationFillMode: 'both' }}
            >
              <Heart size={28} fill="currentColor" strokeWidth={0} className="text-rose-400" aria-hidden />
              <span className="text-rose-300 font-bold text-sm">Tô Apaixonado</span>
            </button>
            <button
              onClick={() => void handlePedirComVibe('sofrencia')}
              className="flex flex-col items-center gap-2 py-5 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 transition-all animate-fade-in-up"
              style={{ animationDelay: '160ms', animationFillMode: 'both' }}
            >
              <Snowflake size={28} className="text-cyan-300" aria-hidden />
              <span className="text-cyan-300 font-bold text-sm">Tô Sofrendo</span>
            </button>
          </div>
          <button
            onClick={cancelarVibeSelection}
            className="w-full py-3 rounded-2xl bg-white/[0.04] text-white/50 text-sm font-semibold active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 transition-transform"
          >
            Cancelar
          </button>
        </div>
      </MobileSheet>
    </div>
  );
}
